import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Ticket as TicketIcon,
  LogOut,
  Search,
  RefreshCw,
  Mail,
  Phone,
  Building,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Users,
  TrendingUp,
  BarChart3,
  Megaphone
} from 'lucide-react';
import axios from 'axios';
import { TechniciansPage } from '../components/TechniciansPage';
import { SalesCRMPage } from '../components/SalesCRMPage';
import { SEOAnalytics } from '../components/SEOAnalytics';
import { AnnouncementAdmin } from '../components/AnnouncementAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const priorityColors = {
  'Basse': 'bg-gray-100 text-gray-800',
  'Normale': 'bg-[#FF6B35]/10 text-[#FF6B35]',
  'Haute': 'bg-orange-100 text-orange-800',
  'Urgente': 'bg-red-100 text-red-800'
};

const statusColors = {
  'Nouveau': 'bg-purple-100 text-purple-800',
  'En cours': 'bg-[#FF6B35]/10 text-[#FF6B35]',
  'Résolu': 'bg-green-100 text-green-800',
  'Fermé': 'bg-gray-100 text-gray-800'
};

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    search: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return false;
    }

    try {
      await axios.get(`${API}/auth/verify`, getAuthHeaders());
      return true;
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      navigate('/admin');
      return false;
    }
  };

  const loadTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, getAuthHeaders());
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error("Erreur lors du chargement des tickets");
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/tickets/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      const isAuth = await checkAuth();
      if (isAuth) {
        await Promise.all([loadTickets(), loadStats()]);
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    toast.success("Déconnexion réussie");
    navigate('/admin');
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([loadTickets(), loadStats()]);
    setLoading(false);
    toast.success("Données actualisées");
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDialog(true);
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { status: newStatus },
        getAuthHeaders()
      );
      toast.success("Statut mis à jour");
      await loadTickets();
      await loadStats();
      
      // Update selected ticket if open
      if (selectedTicket && selectedTicket.id === ticketId) {
        const updatedTicket = tickets.find(t => t.id === ticketId);
        setSelectedTicket({ ...updatedTicket, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddNote = async (ticketId, note) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { admin_notes: note },
        getAuthHeaders()
      );
      toast.success("Note ajoutée");
      await loadTickets();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error("Erreur lors de l'ajout de la note");
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filters.status && filters.status !== 'all' && ticket.status !== filters.status) return false;
    if (filters.priority && filters.priority !== 'all' && ticket.priority !== filters.priority) return false;
    if (filters.category && filters.category !== 'all' && ticket.category !== filters.category) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        ticket.name.toLowerCase().includes(search) ||
        ticket.email.toLowerCase().includes(search) ||
        ticket.subject.toLowerCase().includes(search) ||
        ticket.company.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#FF6B35] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="glass border-b border-white/10 sticky top-0 z-10 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B35] to-[#FF8555] rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold">IS</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">CRM Innova-Solution</h1>
                <p className="text-sm text-gray-400">
                  {localStorage.getItem('adminEmail')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="tickets" className="flex items-center gap-2">
              <TicketIcon className="w-4 h-4" />
              Tickets IT
            </TabsTrigger>
            <TabsTrigger value="technicians" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Techniciens
            </TabsTrigger>
            <TabsTrigger value="sales" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              CRM Ventes
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics SEO
            </TabsTrigger>
            <TabsTrigger value="announcement" className="flex items-center gap-2">
              <Megaphone className="w-4 h-4" />
              Annonce
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tickets">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-white">{stats.total}</div>
                <div className="text-sm text-gray-400">Total</div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-purple-400">{stats.nouveau}</div>
                <div className="text-sm text-gray-400">Nouveau</div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-[#FF6B35]">{stats.en_cours}</div>
                <div className="text-sm text-gray-400">En cours</div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-400">{stats.resolu}</div>
                <div className="text-sm text-gray-400">Résolu</div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-gray-300">{stats.ferme}</div>
                <div className="text-sm text-gray-400">Fermé</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6 bg-[#121212] border-white/10">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm mb-2 block text-gray-300">Recherche</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Nom, email, sujet..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10 bg-black/50 border-white/10 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-2 block text-gray-300">Statut</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value })}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="Nouveau">Nouveau</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="Résolu">Résolu</SelectItem>
                    <SelectItem value="Fermé">Fermé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm mb-2 block text-gray-300">Priorité</Label>
                <Select
                  value={filters.priority}
                  onValueChange={(value) => setFilters({ ...filters, priority: value })}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="Basse">Basse</SelectItem>
                    <SelectItem value="Normale">Normale</SelectItem>
                    <SelectItem value="Haute">Haute</SelectItem>
                    <SelectItem value="Urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm mb-2 block text-gray-300">Catégorie</Label>
                <Select
                  value={filters.category}
                  onValueChange={(value) => setFilters({ ...filters, category: value })}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="Matériel">Matériel</SelectItem>
                    <SelectItem value="Logiciel">Logiciel</SelectItem>
                    <SelectItem value="Réseau">Réseau</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Impression">Impression</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Tickets de Support ({filteredTickets.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredTickets.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <TicketIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun ticket trouvé</p>
                </div>
              ) : (
                filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="border border-white/10 rounded-lg p-4 hover:border-[#FF6B35]/50 transition-all cursor-pointer bg-[#1a1a1a]"
                    onClick={() => handleViewTicket(ticket)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="font-mono text-sm text-gray-400">
                            #{ticket.id.substring(0, 8)}
                          </span>
                          <Badge className={statusColors[ticket.status]}>
                            {ticket.status}
                          </Badge>
                          <Badge className={priorityColors[ticket.priority]}>
                            {ticket.priority}
                          </Badge>
                          <Badge variant="outline" className="border-white/20 text-gray-300">{ticket.category}</Badge>
                        </div>
                        <h3 className="font-bold text-white mb-1">{ticket.subject}</h3>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {ticket.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            {ticket.company}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(ticket.created_at).toLocaleString('fr-CA')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

      {/* Ticket Detail Dialog */}
      {selectedTicket && (
        <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Ticket #{selectedTicket.id.substring(0, 8)}
                <Badge className={statusColors[selectedTicket.status]}>
                  {selectedTicket.status}
                </Badge>
                <Badge className={priorityColors[selectedTicket.priority]}>
                  {selectedTicket.priority}
                </Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Client Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Client</Label>
                  <p className="font-medium">{selectedTicket.name}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Entreprise</Label>
                  <p className="font-medium">{selectedTicket.company}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Email</Label>
                  <a
                    href={`mailto:${selectedTicket.email}`}
                    className="font-medium text-[#FF6B35] hover:underline flex items-center gap-1"
                  >
                    <Mail className="w-4 h-4" />
                    {selectedTicket.email}
                  </a>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Téléphone</Label>
                  <a
                    href={`tel:${selectedTicket.phone}`}
                    className="font-medium text-[#FF6B35] hover:underline flex items-center gap-1"
                  >
                    <Phone className="w-4 h-4" />
                    {selectedTicket.phone}
                  </a>
                </div>
              </div>

              {/* Ticket Details */}
              <div>
                <Label className="text-sm text-gray-600">Sujet</Label>
                <p className="font-medium text-lg">{selectedTicket.subject}</p>
              </div>

              <div>
                <Label className="text-sm text-gray-600">Description</Label>
                <p className="mt-2 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                  {selectedTicket.description}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Catégorie</Label>
                  <p className="font-medium">{selectedTicket.category}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Créé le</Label>
                  <p className="font-medium">
                    {new Date(selectedTicket.created_at).toLocaleString('fr-CA')}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Mis à jour le</Label>
                  <p className="font-medium">
                    {new Date(selectedTicket.updated_at).toLocaleString('fr-CA')}
                  </p>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedTicket.admin_notes && (
                <div>
                  <Label className="text-sm text-gray-600">Notes Admin</Label>
                  <p className="mt-2 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    {selectedTicket.admin_notes}
                  </p>
                </div>
              )}

              {/* Status Actions */}
              <div>
                <Label className="text-sm text-gray-600 mb-3 block">Changer le statut</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={selectedTicket.status === 'Nouveau' ? 'default' : 'outline'}
                    onClick={() => handleUpdateTicketStatus(selectedTicket.id, 'Nouveau')}
                    className="flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" />
                    Nouveau
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedTicket.status === 'En cours' ? 'default' : 'outline'}
                    onClick={() => handleUpdateTicketStatus(selectedTicket.id, 'En cours')}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    En cours
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedTicket.status === 'Résolu' ? 'default' : 'outline'}
                    onClick={() => handleUpdateTicketStatus(selectedTicket.id, 'Résolu')}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Résolu
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedTicket.status === 'Fermé' ? 'default' : 'outline'}
                    onClick={() => handleUpdateTicketStatus(selectedTicket.id, 'Fermé')}
                    className="flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Fermé
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
          </TabsContent>

          <TabsContent value="technicians">
            <TechniciansPage getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="sales">
            <SalesCRMPage getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="analytics">
            <SEOAnalytics />
          </TabsContent>

          <TabsContent value="announcement">
            <AnnouncementAdmin />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
